<template>
  <v-container class="pa-0">
    <v-row dense row wrap justify="center">
      <v-col class="col-12">
        <div class="text-center places-slider-text">
          Within <span>{{ radius }}</span>
          {{ poisDistUnits === "mi" ? "Mile" : "Kilometer" }} Radius
        </div>
        <v-slider
          v-model="radius"
          :min="1"
          :max="radiusMax"
          class="places-slider"
          @change="handleSliderChange"
        ></v-slider>
      </v-col>
    </v-row>

    <v-row dense row wrap justify="center">
      <v-col class="col-12">
        <div
          class="text-center places-count-text"
          :class="{ 'red--text': maxPlacesSelected }"
        >
          {{ placesSelectedCount }} out of {{ maxPlacesAllowed }} selected
        </div>
      </v-col>
    </v-row>

    <v-row dense row wrap>
      <v-col class="col-12">
        <v-container v-for="place in places" :key="place.uniqueId" class="pa-0">
          <v-row dense row wrap class="places-selector-row">
            <v-col v-if="!place.isDefaultLayer" class="col-10 places-item">
              <img :src="getImgSrc(place)" class="pull-left" />
              <div class="ml-4 places-name-label pull-left">
                {{ place.name }}
              </div>
              <div class="clearfix"></div>
            </v-col>
            <v-col v-if="!place.isDefaultLayer" class="col-2">
              <v-switch
                v-model="enabled"
                :value="place.uniqueId"
                class="pa-0 ma-0 mt-1"
                solo
                item-text="display"
              ></v-switch>
            </v-col>
          </v-row>
          <v-row
            v-if="enabled.includes(place.uniqueId)"
            dense
            row
            wrap
            class="places-item-row"
          >
            <v-col class="col-12">
              <div
                v-for="(item, index) in place.data.features"
                :key="index"
                class="places-item-names ml-3 pb-1"
              >
                <label class="places-item-label">
                  <input
                    type="checkbox"
                    name="checkbox"
                    value="text"
                    class="places-item-checkbox"
                    :disabled="isItemDisabled(item)"
                    :checked="getItemChecked(item)"
                    @change="handlePlaceChange(item)"
                  />
                  <span>{{ getDisplayValue(item, place) }}</span>
                </label>
              </div>
            </v-col>
          </v-row>
        </v-container>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import lodashGet from "lodash/get";
import lodashDebounce from "lodash/debounce";
import lodashCloneDeep from "lodash/cloneDeep";

import { mapGetters } from "vuex";

import { GET_PLACES } from "@/store/actions.type";
import { MODULE_PREFIX_PLACES, MODULE_PREFIX_USER } from "@/store";
import { ICONS } from "@/common/constants/icons";
import { PLACES_IMAGE_URL } from "@/common/constants/api";
import {
  SEARCH_PLACES_RADIUS,
  SEARCH_PLACES_RADIUS_MAX,
  SEARCH_PLACES_MAX_ALLOWED,
} from "@/common/constants/savedSearch";

const EMITTED_ACTIONS = {
  CHANGE: "change",
};

export default {
  name: "SearchFilterPlaces",
  props: {
    selected: {
      type: Array,
    },
    reset: {
      type: String,
    },
    selectedUnitsRadius: {
      type: Object,
    },
  },
  data() {
    const placesSelected = lodashCloneDeep(this.selected);
    return {
      radius: SEARCH_PLACES_RADIUS,
      radiusMax: SEARCH_PLACES_RADIUS_MAX,
      enabled: [],
      maxPlacesAllowed: SEARCH_PLACES_MAX_ALLOWED,
      placesSelected: placesSelected ? placesSelected : [],
      poisDistUnits: false,
    };
  },
  computed: {
    ...mapGetters({
      places: MODULE_PREFIX_PLACES + "places",
      preferences: MODULE_PREFIX_USER + "preferences",
    }),
    placesSelectedCount() {
      return this.placesSelected && this.placesSelected.length
        ? this.placesSelected.length
        : 0;
    },
    maxPlacesSelected() {
      return this.maxPlacesAllowed === this.placesSelectedCount;
    },
  },
  async mounted() {
    await this.getPlaces();
    this.getPrefs();
  },
  methods: {
    async getPlaces() {
      await this.$store.dispatch(MODULE_PREFIX_PLACES + GET_PLACES);
    },
    async getPrefs() {
      if (lodashGet(this.preferences, "poisDistUnits")) {
        let unit = lodashGet(this.preferences, "poisDistUnits");
        this.poisDistUnits = unit === "miles" ? "mi" : "km";
      } else {
        this.poisDistUnits = "mi";
      }
    },
    getImgSrc(place) {
      if (!place) {
        return null;
      }

      let imgSrcActive;
      let imgSrcInactive;
      if (
        lodashGet(place, "icons") &&
        place.icons.toLowerCase() !== "default" &&
        lodashGet(place, "group")
      ) {
        const URL = PLACES_IMAGE_URL;
        imgSrcActive = `${URL}/${place.group}/${place.icons}.png`;
        imgSrcInactive = `${URL}/${place.group}/${place.icons}.png`;
      } else {
        const ICON_ACTIVE = ICONS.places["default"].active;
        const ICON_INACTIVE = ICONS.places["default"].inactive;
        imgSrcActive = require(`@/assets/images${ICON_ACTIVE}`);
        imgSrcInactive = require(`@/assets/images${ICON_INACTIVE}`);
      }

      return place.enabled ? imgSrcInactive : imgSrcActive;
    },
    getDisplayValue(item, place) {
      if (
        lodashGet(place, "displayFields") &&
        Array.isArray(place.displayFields) &&
        place.displayFields.length
      ) {
        const values = [];
        place.displayFields.forEach((field) => {
          if (field && lodashGet(item, `properties[${field}]`)) {
            values.push(item.properties[field]);
          }
        });
        return values.join(" - ");
      }
      return null;
    },
    getItemChecked(item) {
      if (item && item.id) {
        return this.placesSelected.includes(item.id) ? "checked" : "";
      }
      return "";
    },
    isItemDisabled(item) {
      if (this.maxPlacesSelected && !this.placesSelected.includes(item.id)) {
        return true;
      }
      return false;
    },
    handleSliderChange() {
      if (this.placesSelected) {
        this.emitChange();
      }
    },
    handlePlaceChange(item) {
      if (!this.placesSelected) {
        this.placesSelected = [];
      }
      if (this.placesSelected.includes(item.id)) {
        const index = this.placesSelected.indexOf(item.id);
        if (index > -1) {
          this.placesSelected.splice(index, 1);
        }
      } else {
        this.placesSelected.push(item.id);
      }
      this.emitChange();
    },
    emitChange: lodashDebounce(function () {
      this.$emit(EMITTED_ACTIONS.CHANGE, {
        poisDistUnits: this.poisDistUnits,
        radius: this.radius,
        places: this.placesSelected,
      });
    }, 800),
  },
  watch: {
    selected() {
      const placesSelected = lodashCloneDeep(this.selected);
      this.placesSelected = placesSelected ? placesSelected : [];
    },
    selectedUnitsRadius() {
      this.radius = this.selectedUnitsRadius.radius;
      this.poisDistUnits = this.selectedUnitsRadius.poisDistUnits;

      if (this.placesSelected.length) {
        const names = [];
        const placeUID = {};
        this.enabled = [];

        this.places.forEach((place) => {
          placeUID[place.data.features[0].id.split("-")[0]] = place.uniqueId;
        });
        this.placesSelected.forEach((place) => {
          const name = place.split("-")[0];
          if (!names.includes(name)) {
            names.push(name);
            this.enabled.push(placeUID[name]);
          }
        });
      }
    },
    reset() {
      this.placesSelected = [];
      this.enabled = [];
      if (lodashGet(this.preferences, "poisDistUnits")) {
        let unit = lodashGet(this.preferences, "poisDistUnits");
        this.poisDistUnits = unit === "miles" ? "mi" : "km";
      } else {
        this.poisDistUnits = "mi";
      }
      this.radius = 10;
      this.emitChange();
    },
  },
  beforeDestroy() {
    this.placesSelected = [];
  },
};
</script>

<style>
.places-slider .v-input__slot {
  margin-bottom: 0;
}
.places-slider .v-messages {
  display: none;
}
</style>

<style scoped>
.places-slider-text {
  font-size: 14px;
}
.places-count-text {
  font-size: 12px;
}
.places-item {
  cursor: pointer;
}
.places-selector-row {
  height: 40px;
}
.places-item-row {
  max-height: 250px;
  overflow-y: auto;
}
.places-name-label {
  color: #1c1c1c;
  font-size: 14px !important;
  font-family: TruenoRg;
  margin: 0 0 0 5px !important;
  position: relative;
  top: -4px;
  width: 160px;
}
.places-item-names {
  font-size: 12px;
}
.places-item-label {
  display: flex;
  align-items: baseline;
  cursor: pointer;
}
.places-item-checkbox {
  margin-right: 8px;
  position: relative;
  top: 2px;
  cursor: pointer;
}
.places-count-total {
  font-size: 12px;
  position: relative;
  top: -1px;
  font-family: TruenoLt;
  padding-left: 5px;
}
</style>
